import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components';
import { adLanguageLabel } from '../../ad-language';
import { LANGUAGE_NAME_MAX } from '../../list-name-api';
import { ListsMeta, ListsTitles } from '../GameLists/GameLists.styles';
import {
  Actions,
  DONE_OVER_GLYPH_PX,
  DoneBtn,
  EDIT_GLYPH_PX,
  EditGlyph,
  EditLine,
  FieldSizer,
  HeadRow,
  LangChip,
  LangLine,
  MetaLine,
  NameField,
  OwnTag,
  RenameBtn,
  SearchSlot,
  Side,
  TitleLine,
  TitleText,
} from './ListHeading.styles';

export type ListHeadingProps = {
  /** The list's name as the tab shows it. */
  title: string;
  /** How many games the list holds; nothing is said while that is not known. */
  count?: number | null;
  /** Renames the list (one of the owner's own): a click on its name edits it in place, Enter or leaving the field keeps
   *  the new name, Escape keeps the old one. Without it the name is plain text. The new name is shown until what it
   *  returns settles (by then `title` carries it, or, the rename failed, the old one again). */
  onRename?: (name: string, edits?: Record<string, string>) => unknown;
  /** The list's name in each website language, and the languages it is named in (English first). With `onRenameIn`
   *  and more than one language, an edit of the name offers them under the field: EN (the default) edits the name
   *  itself, which `onRename` names in every language again; another language edits that language's name alone.
   *  Switching between them keeps what was typed, and the edit's end keeps it all at once: `onRename` with the
   *  languages typed when the name itself changed too, else `onRenameIn` with them. */
  names?: Record<string, string> | null;
  languages?: readonly string[];
  /** Keeps the names typed in some languages (those alone), the name itself unchanged. */
  onRenameIn?: (edits: Record<string, string>) => unknown;
  /** A write is running: the name is not offered for editing. */
  disabled?: boolean;
  /** One of the owner's own lists: "User list" after its count (the column of lists no longer marks it). */
  own?: boolean;
  /** The list's actions, on its name's row, at its right. */
  actions?: React.ReactNode;
  /** Takes the element the list's search is drawn into (a portal's target), on the name's row at its right, before the
   *  actions; without it there is none. */
  searchSlot?: (el: HTMLDivElement | null) => void;
};

/**
 * The open list's heading over its games, set as the column of lists heads the page: the list's name, and how many
 * games it holds, with the list's search and its actions at the row's right (under the name when the row is too narrow
 * for them). One of the owner's own lists carries the edit glyph beside its name, which a click turns into a field.
 */
export const ListHeading = ({
  title,
  count,
  onRename,
  names = null,
  languages = [],
  onRenameIn,
  disabled = false,
  own = false,
  actions,
  searchSlot,
}: ListHeadingProps): React.ReactElement => {
  const { t } = useTranslation();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  // The language the field edits; null: the name itself, which is English (EN's chip).
  const [lang, setLang] = React.useState<string | null>(null);
  // What this edit typed in the languages switched away from, by language ('' the name itself).
  const [typed, setTyped] = React.useState<Record<string, string>>({});
  const langLineRef = React.useRef<HTMLSpanElement>(null);
  const perLanguage = Boolean(onRenameIn) && languages.length > 1;
  /** The name in a language as the site shows it: its own, else its English one, else the name itself. */
  const nameIn = (code: string): string => names?.[code] || names?.en || title;
  // The name just kept, shown while it is being stored, so the old one does not come back meanwhile.
  const [pending, setPending] = React.useState<string | null>(null);
  const fieldRef = React.useRef<HTMLInputElement>(null);
  const nameBtnRef = React.useRef<HTMLButtonElement>(null);
  // Enter or Escape ends the edit before the field goes, and the field going would end it again on its blur.
  const doneRef = React.useRef(false);
  // Ended from the keyboard: the focus goes back to the name (a blur has already taken it elsewhere).
  const refocusRef = React.useRef(false);
  const shown = pending ?? title;
  // An edit has ended on this heading: its name fades back in where the field was.
  const [back, setBack] = React.useState(false);

  React.useEffect(() => {
    if (editing) {
      fieldRef.current?.focus();
      fieldRef.current?.select();
    } else if (refocusRef.current) {
      refocusRef.current = false;
      nameBtnRef.current?.focus();
    }
  }, [editing]);

  // The field keeps the name's width (the user, 2026-10-21), its check where the edit glyph was.
  const [editWidth, setEditWidth] = React.useState<number | null>(null);
  const start = (): void => {
    doneRef.current = false;
    setLang(null);
    setTyped({});
    setDraft(title);
    const btn = nameBtnRef.current;
    setEditWidth(btn ? btn.getBoundingClientRect().width + DONE_OVER_GLYPH_PX : null);
    setEditing(true);
  };
  const finish = (keep: boolean, fromKey = false): void => {
    if (doneRef.current) return;
    doneRef.current = true;
    refocusRef.current = fromKey;
    setEditing(false);
    setBack(true);
    if (!keep) return;
    const all = { ...typed, [lang ?? '']: draft };
    const name = (all[''] ?? title).trim();
    const renamed = name !== '' && name !== title.trim();
    // Each language typed otherwise than the site shows it in that language.
    const edits = Object.fromEntries(
      Object.entries(all)
        .map(([code, value]) => [code, value.trim()] as const)
        .filter(([code, value]) => code !== '' && value !== '' && value !== nameIn(code).trim()),
    );
    const edited = Object.keys(edits).length > 0;
    if (renamed && onRename) {
      setPending(name);
      void Promise.resolve(onRename(name, edited ? edits : undefined)).finally(() => setPending(null));
    } else if (edited) void onRenameIn?.(edits);
  };
  /** The field edits another language: what was typed in the one it leaves is kept for the edit's end. */
  const switchTo = (code: string | null): void => {
    const next = { ...typed, [lang ?? '']: draft };
    setTyped(next);
    setLang(code);
    setDraft(next[code ?? ''] ?? (code === null ? title : nameIn(code)));
    fieldRef.current?.focus();
  };
  // The language switched to is selected once the field shows its name, so typing replaces it as at an edit's start
  // (selected in the click, the old text would be, and the new value then puts the caret at its end).
  React.useLayoutEffect(() => {
    if (editing) fieldRef.current?.select();
  }, [lang, editing]);

  const renderName = (): React.ReactNode => {
    if (editing) {
      return (
        <EditLine style={editWidth ? { minWidth: editWidth } : undefined}>
          <FieldSizer data-value={draft}>
            <NameField
              ref={fieldRef}
              value={draft}
              // No width of its own: the sizer gives it the name's (an input's default is some 20 characters).
              size={1}
              aria-label={
                lang === null ? t('List name') : t('List name in {{language}}', { language: adLanguageLabel(lang) })
              }
              // The name as the list editor caps it; a language's as the service takes it (a translation can be longer).
              maxLength={lang === null ? 60 : LANGUAGE_NAME_MAX}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  // The focus goes back to the name as the edit ends: this Enter must not go on to click it, editing again.
                  event.preventDefault();
                  finish(true, true);
                } else if (event.key === 'Escape') finish(false, true);
              }}
              // Moving to a language chip keeps the edit going.
              onBlur={(event) => {
                if (!langLineRef.current?.contains(event.relatedTarget as Node | null)) finish(true);
              }}
            />
          </FieldSizer>
          {/* Its press keeps the focus in the field (no blur ending the edit first), then keeps the name. Out of the Tab
              order while the languages are offered, so Tab goes from the field to them (Enter keeps the name). */}
          <DoneBtn
            type="button"
            tabIndex={perLanguage ? -1 : undefined}
            aria-label={t('Done')}
            title={t('Done')}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => finish(true, true)}
          >
            <Icon name="check" size={14} />
          </DoneBtn>
        </EditLine>
      );
    }
    if (onRename) {
      return (
        <TitleLine $back={back}>
          <RenameBtn
            ref={nameBtnRef}
            type="button"
            // Not `disabled`: the focus comes back to it as an edit ends, while the new name is being stored.
            aria-disabled={disabled || pending !== null || undefined}
            $dim={disabled && pending === null}
            aria-label={t('Rename {{name}}', { name: shown })}
            onClick={disabled || pending !== null ? undefined : start}
          >
            <TitleText text={shown} raw tag="span" />
            <EditGlyph aria-hidden="true">
              <Icon name="edit" size={EDIT_GLYPH_PX} />
            </EditGlyph>
          </RenameBtn>
        </TitleLine>
      );
    }
    return (
      <TitleLine>
        <TitleText text={title} raw tag="span" />
      </TitleLine>
    );
  };

  return (
    <HeadRow>
      <ListsTitles>
        {renderName()}
        {editing && perLanguage ? (
          <LangLine ref={langLineRef} role="group" aria-label={t('Languages')}>
            {languages.map((code) => {
              // EN edits the name itself.
              const target = code === 'en' ? null : code;
              return (
                <LangChip
                  key={code}
                  type="button"
                  $on={lang === target}
                  aria-pressed={lang === target}
                  title={adLanguageLabel(code)}
                  // Its press keeps the focus in the field (no blur ending the edit).
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => switchTo(target)}
                  onBlur={(event) => {
                    const next = event.relatedTarget as Node | null;
                    if (next !== fieldRef.current && !langLineRef.current?.contains(next)) finish(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') finish(false, true);
                  }}
                >
                  {code.toUpperCase()}
                </LangChip>
              );
            })}
          </LangLine>
        ) : count == null && !own ? null : (
          <MetaLine>
            {count == null ? null : count === 1 ? (
              <ListsMeta text="1 game" tag="span" />
            ) : (
              <ListsMeta text="{{n}} games" placeholders={{ n: count }} tag="span" />
            )}
            {own ? <OwnTag text="User list" tag="span" /> : null}
          </MetaLine>
        )}
      </ListsTitles>
      {searchSlot || actions ? (
        <Side $away={editing}>
          {searchSlot ? <SearchSlot ref={searchSlot} /> : null}
          {actions ? <Actions>{actions}</Actions> : null}
        </Side>
      ) : null}
    </HeadRow>
  );
};

export default ListHeading;
